import { useEffect, useMemo, useRef, useState } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { Package, Award, MapPin, Users } from 'lucide-react'
import SectionHeading from './SectionHeading'
import ApiURl from '../controllers/Api'

gsap.registerPlugin(ScrollTrigger)

const MAROON = 'var(--user-maroon, #2B0000)'
const MAROON_DEEP = 'var(--user-maroon-deep, #2B0000)'
const GOLD = 'var(--user-gold, #d4af37)'
const GOLD_SOFT = '#e9cd7a'
const CREAM = '#ded6c8'

const iconMap = {
  package: Package,
  award: Award,
  mapPin: MapPin,
  users: Users,
}

const defaultContent = {
  section_title: 'WHY KANTHI',
  section_subtitle: 'Our legacy in numbers',
  main_title: 'Built through decades of',
  highlighted_title: 'excellence.',
  description:
    'Our journey is defined by product variety, industry experience, customer trust and a strong presence across India.',
  stats: [
    {
      icon: 'package',
      value: '3,000+',
      title: 'Products',
      text: 'A wide product range created to serve different customer needs.',
    },
    {
      icon: 'award',
      value: '40+',
      title: 'Years of Experience',
      text: 'Four decades of trust, craftsmanship and industry expertise.',
    },
    {
      icon: 'mapPin',
      value: '19+',
      title: 'States',
      text: 'A strong and growing presence across multiple regions in India.',
    },
    {
      icon: 'users',
      value: '150,000+',
      title: 'Happy Customers',
      text: 'Trusted by thousands of customers through quality and reliability.',
    },
  ],
}

const defaultImages = {
  primary:
    'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=format&fit=crop&w=1200&q=85',

  secondary:
    'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&w=1000&q=85',
}

export default function WhyKanthi() {
  const sectionRef = useRef(null)
  const backImageRef = useRef(null)
  const frontImageRef = useRef(null)
  const contentRef = useRef(null)
  const numberRefs = useRef([])

  const [content, setContent] = useState(defaultContent)
  const [images, setImages] = useState(defaultImages)

  /* ============================================================
     LOAD CONTENT
     ============================================================ */

  useEffect(() => {
    let alive = true

    fetch(`${ApiURl}/getWhyKanthi.php`)
      .then((response) => response.json())
      .then((response) => {
        if (!alive || !response?.success || !response?.data) return

        const data = response.data

        setContent({
          ...defaultContent,
          ...data,
          stats:
            Array.isArray(data.stats) && data.stats.length > 0
              ? data.stats
              : defaultContent.stats,
        })

        setImages({
          primary:
            data.primary_image_url ||
            defaultImages.primary,

          secondary:
            data.secondary_image_url ||
            defaultImages.secondary,
        })
      })
      .catch((error) => {
        console.error(
          'Error loading Why Kanthi section:',
          error
        )
      })

    return () => {
      alive = false
    }
  }, [])

  /* ============================================================
     STATS
     ============================================================ */

  const stats = useMemo(
    () =>
      (content.stats || defaultContent.stats).map((stat) => ({
        ...stat,
        icon: stat.icon || 'package',
      })),
    [content.stats]
  )

  const parseStatValue = (value) => {
    const raw = String(value || '')

    const numeric =
      Number(raw.replace(/[^\d.]/g, '')) || 0

    const suffix =
      raw.replace(/[\d.,\s]/g, '')

    return {
      numeric,
      suffix,
    }
  }

  /* ============================================================
     GSAP
     ============================================================ */

  useEffect(() => {
    const section = sectionRef.current
    const backImage = backImageRef.current
    const frontImage = frontImageRef.current
    const contentElement = contentRef.current

    if (
      !section ||
      !backImage ||
      !frontImage ||
      !contentElement
    ) {
      return undefined
    }

    const prefersReducedMotion =
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches

    const finalValues = stats.map(
      (stat) =>
        parseStatValue(stat.value).numeric
    )

    const formatNumber = (number) => {
      return Math.round(number).toLocaleString('en-IN')
    }

    if (prefersReducedMotion) {
      finalValues.forEach((value, index) => {
        const element =
          numberRefs.current[index]

        if (element) {
          element.textContent =
            formatNumber(value)
        }
      })

      return undefined
    }

    const context = gsap.context(() => {
      finalValues.forEach((value, index) => {
        const element =
          numberRefs.current[index]

        if (element) {
          element.textContent = '0'
        }
      })

      const timeline = gsap.timeline({
        paused: true,
      })

      timeline
        .from('[data-anim="eyebrow-line"]', {
          scaleX: 0,
          opacity: 0,
          duration: 0.55,
          stagger: 0.1,
          ease: 'power3.out',
        })

        .from(
          '[data-anim="heading"]',
          {
            y: 22,
            opacity: 0,
            duration: 0.65,
            ease: 'power3.out',
          },
          '-=0.3'
        )

        .from(
          '[data-anim="subtitle"]',
          {
            y: 16,
            opacity: 0,
            duration: 0.55,
            ease: 'power3.out',
          },
          '-=0.35'
        )

      timeline.fromTo(
        backImage,
        {
          x: -80,
          y: 60,
          autoAlpha: 0,
          clipPath:
            'inset(0% 100% 0% 0%)',
        },
        {
          x: 0,
          y: 0,
          autoAlpha: 1,
          clipPath:
            'inset(0% 0% 0% 0%)',
          duration: 1,
          ease: 'power3.out',
        },
        0.35
      )

      timeline.fromTo(
        frontImage,
        {
          x: -60,
          y: 85,
          autoAlpha: 0,
          clipPath:
            'inset(100% 0% 0% 0%)',
        },
        {
          x: 0,
          y: 0,
          autoAlpha: 1,
          clipPath:
            'inset(0% 0% 0% 0%)',
          duration: 1,
          ease: 'power3.out',
        },
        0.5
      )

      timeline.fromTo(
        contentElement.children,
        {
          y: 36,
          autoAlpha: 0,
        },
        {
          y: 0,
          autoAlpha: 1,
          duration: 0.8,
          stagger: 0.11,
          ease: 'power3.out',
        },
        0.55
      )

      finalValues.forEach(
        (target, index) => {
          const counter = {
            value: 0,
          }

          const numberElement =
            numberRefs.current[index]

          timeline.to(
            counter,
            {
              value: target,
              duration: 1.25,
              ease: 'power2.out',

              onStart: () => {
                counter.value = 0

                if (numberElement) {
                  numberElement.textContent =
                    '0'
                }
              },

              onUpdate: () => {
                if (numberElement) {
                  numberElement.textContent =
                    formatNumber(
                      counter.value
                    )
                }
              },

              onComplete: () => {
                if (numberElement) {
                  numberElement.textContent =
                    formatNumber(target)
                }
              },
            },
            0.5 + index * 0.1
          )
        }
      )

      ScrollTrigger.create({
        trigger: section,
        start: 'top 72%',

        onEnter: () => {
          timeline.restart()
        },

        invalidateOnRefresh: true,
      })

      ScrollTrigger.create({
        trigger: section,
        start: 'bottom 92%',

        onEnterBack: () => {
          timeline.restart()
        },

        invalidateOnRefresh: true,
      })
    }, section)

    const refreshTimer =
      window.setTimeout(() => {
        ScrollTrigger.refresh()
      }, 300)

    const handleLoad = () => {
      ScrollTrigger.refresh()
    }

    window.addEventListener(
      'load',
      handleLoad
    )

    return () => {
      window.clearTimeout(
        refreshTimer
      )

      window.removeEventListener(
        'load',
        handleLoad
      )

      context.revert()
    }
  }, [stats])

  /* ============================================================
     RENDER
     ============================================================ */

  return (
    <section
      ref={sectionRef}
      className="
        relative
        flex
        flex-col
        justify-center
        overflow-hidden

        py-6

        sm:py-8

        lg:min-h-[100vh]
        lg:py-8
      "
      style={{
        backgroundColor: MAROON,
        color: CREAM,
      }}
    >
      {/* ======================================================
          SECTION HEADING

          Smaller spacing on mobile.
          Desktop unchanged.
          ====================================================== */}

      <div
        className="
          relative
          mx-auto
          mb-5
          w-full
          max-w-full
          overflow-hidden
          px-4

          sm:mb-6
          sm:px-5

          lg:mb-7
          lg:overflow-visible
        "
      >
        <SectionHeading
          title={content.section_title}
          subtitle={content.section_subtitle}
        />
      </div>

      {/* ======================================================
          MAIN AREA
          ====================================================== */}

      <div className="relative w-full max-w-full overflow-hidden">

        <div
          className="
            grid
            min-w-0
            items-center

            gap-5

            sm:gap-7

            lg:grid-cols-[minmax(0,calc(50vw-1.75rem))_minmax(0,1fr)]
            lg:gap-10
          "
        >
          {/* ==================================================
              LEFT IMAGES

              Mobile made substantially smaller.
              ALL lg/xl values preserved.
              ================================================== */}

          <div className="relative order-2 lg:order-1">

            <div
              className="
                relative
                mr-auto

                h-[255px]
                w-full
                max-w-[430px]

                sm:h-[330px]
                sm:max-w-[560px]

                lg:h-[385px]
                lg:max-w-[600px]

                xl:h-[410px]
                xl:max-w-[620px]
              "
            >
              {/* BACK IMAGE */}

              <div
                ref={backImageRef}
                className="
                  absolute
                  bottom-0
                  left-0

                  h-[195px]
                  w-[57%]

                  overflow-hidden
                  border

                  sm:h-[255px]

                  lg:h-[310px]

                  xl:h-[335px]
                "
                style={{
                  borderColor:
                    'rgba(212,175,55,0.18)',

                  backgroundColor:
                    MAROON_DEEP,

                  boxShadow:
                    '0 20px 55px rgba(0,0,0,0.35)',
                }}
              >
                <img
                  src={images.secondary}
                  alt="Premium manufacturing craftsmanship"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />

                <div
                  className="
                    pointer-events-none
                    absolute
                    inset-0
                  "
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(26,0,0,0.04), rgba(26,0,0,0.38))',
                  }}
                />
              </div>

              {/* FRONT IMAGE */}

              <div
                ref={frontImageRef}
                className="
                  absolute
                  left-[33%]
                  top-0
                  z-10

                  h-[190px]
                  w-[58%]

                  overflow-hidden
                  border

                  sm:h-[245px]

                  lg:h-[300px]

                  xl:h-[325px]
                "
                style={{
                  borderColor:
                    'rgba(212,175,55,0.22)',

                  backgroundColor:
                    MAROON_DEEP,

                  boxShadow:
                    '0 28px 75px rgba(0,0,0,0.48)',
                }}
              >
                <img
                  src={images.primary}
                  alt="Industrial excellence"
                  className="h-full w-full object-cover"
                  loading="eager"
                />

                <div
                  className="
                    pointer-events-none
                    absolute
                    inset-0
                  "
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(26,0,0,0.02), rgba(26,0,0,0.24))',
                  }}
                />
              </div>
            </div>
          </div>

          {/* ==================================================
              RIGHT CONTENT
              ================================================== */}

          <div
            ref={contentRef}
            className="
              relative
              order-1

              mx-auto
              w-full
              max-w-[590px]
              min-w-0

              px-4

              sm:px-6

              lg:order-2
              lg:mx-0
              lg:pl-0
              lg:pr-6

              xl:pr-10
            "
          >
            {/* ================================================
                MAIN HEADING

                Mobile: 25px
                Desktop remains 36/40px
                ================================================ */}

            <h2
              className="
                text-[25px]
                font-semibold
                leading-[1.08]
                tracking-[-0.035em]

                sm:text-[32px]

                lg:text-[36px]

                xl:text-[40px]
              "
              style={{
                color: '#ffffff',
              }}
            >
              {content.main_title}{' '}

              <em
                className="
                  font-serif
                  font-normal
                  italic
                "
                style={{
                  color: GOLD_SOFT,
                }}
              >
                {content.highlighted_title}
              </em>
            </h2>

            {/* ================================================
                DESCRIPTION
                ================================================ */}

            <p
              className="
                mt-2
                max-w-[530px]

                text-[11px]
                leading-[1.55]

                sm:text-[12.5px]

                lg:mt-3
                lg:text-[13px]
                lg:leading-[1.65]

                xl:text-[13.5px]
              "
              style={{
                color:
                  'rgba(222,214,200,0.72)',
              }}
            >
              {content.description}
            </p>

            {/* ================================================
                STATS
                ================================================ */}

            <ul
              className="
                mt-4
                space-y-2.5

                sm:mt-5
                sm:space-y-3

                lg:mt-5
                lg:space-y-3
              "
            >
              {stats.map(
                (stat, index) => {
                  const Icon =
                    iconMap[stat.icon] ||
                    Package

                  const parsedValue =
                    parseStatValue(
                      stat.value
                    )

                  return (
                    <li
                      key={stat.title}
                      className="
                        group
                        flex
                        items-start

                        gap-3

                        sm:gap-4
                      "
                    >
                      {/* ICON */}

                      <div
                        className="
                          flex

                          h-8
                          w-8

                          shrink-0
                          items-center
                          justify-center

                          rounded-full
                          border

                          sm:h-9
                          sm:w-9

                          lg:h-9
                          lg:w-9
                        "
                        style={{
                          borderColor:
                            'rgba(212,175,55,0.48)',

                          backgroundColor:
                            'rgba(212,175,55,0.07)',

                          color: GOLD,
                        }}
                      >
                        <Icon
                          className="
                            h-3.5
                            w-3.5

                            lg:h-3.5
                            lg:w-3.5
                          "
                          strokeWidth={1.8}
                        />
                      </div>

                      {/* TEXT */}

                      <div
                        className="
                          min-w-0
                          flex-1

                          border-b

                          pb-2.5

                          sm:pb-3

                          lg:pb-3
                        "
                      >
                        <div
                          className="
                            flex
                            flex-wrap
                            items-baseline

                            gap-x-2
                            gap-y-1

                            sm:gap-x-3
                          "
                        >
                          {/* NUMBER */}

                          <p
                            className="
                              font-serif

                              text-[21px]
                              font-semibold
                              leading-none
                              tracking-[-0.035em]
                              text-white

                              sm:text-[27px]

                              lg:text-[27px]

                              xl:text-[29px]
                            "
                          >
                            <span
                              ref={(
                                element
                              ) => {
                                numberRefs.current[
                                  index
                                ] =
                                  element
                              }}
                            >
                              0
                            </span>

                            <span
                              className="
                                ml-0.5
                                text-[0.65em]
                              "
                              style={{
                                color: GOLD,
                              }}
                            >
                              {
                                parsedValue.suffix
                              }
                            </span>
                          </p>

                          {/* STAT TITLE */}

                          <h3
                            className="
                              text-[9px]
                              font-semibold
                              uppercase
                              tracking-[0.14em]

                              sm:text-[11px]

                              lg:text-[11px]
                              lg:tracking-[0.16em]

                              xl:text-[12px]
                            "
                            style={{
                              color:
                                GOLD_SOFT,
                            }}
                          >
                            {stat.title}
                          </h3>
                        </div>

                        {/* STAT DESCRIPTION */}

                        <p
                          className="
                            mt-1
                            max-w-[455px]

                            text-[9.5px]
                            leading-[1.45]

                            sm:text-[11px]

                            lg:mt-1.5
                            lg:text-[11px]
                            lg:leading-[1.5]

                            xl:text-[11.5px]
                          "
                          style={{
                            color:
                              'rgba(222,214,200,0.62)',
                          }}
                        >
                          {stat.text}
                        </p>

                        {/* HOVER LINE */}

                        <div
                          className="
                            mt-1.5
                            h-px
                            w-0

                            transition-all
                            duration-500

                            group-hover:w-16

                            lg:mt-2
                          "
                          style={{
                            backgroundColor:
                              GOLD,
                          }}
                        />
                      </div>
                    </li>
                  )
                }
              )}
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}